import { useMemo, useState } from "react";
import emailjs from "@emailjs/browser";
import { X } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { formatPrice, getRentTaxLabel } from "@/utils/price";
import { getLikedPropertiesSummary } from "@/utils/propertyLikes";
import { getAuthSession, updateUserMetadata } from "@/lib/supabaseAuth";

const WHATSAPP_NUMBER = "526673093018";
const DEFAULT_MESSAGE = "Hola, quiero información sobre locales disponibles.";
const CONTACT_LEAD_KEY = "tulocalmx_contact_lead";
const FILTERED_LEAD_PLANS = ["filtrado", "premium", "impulso", "tulocalmx"];

const cleanPhone = (value = "") => String(value).replace(/\D/g, "");

const getSavedLead = () => {
  try {
    return JSON.parse(localStorage.getItem(CONTACT_LEAD_KEY) || "{}");
  } catch (error) {
    return {};
  }
};

export const FloatingWhatsApp = ({ property }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [isSendingLead, setIsSendingLead] = useState(false);
  const [leadError, setLeadError] = useState("");

  const initialLead = useMemo(() => {
    const session = getAuthSession();
    const metadata = session?.user?.user_metadata || {};
    const savedLead = getSavedLead();

    return {
      nombre: metadata.nombre || metadata.full_name || metadata.name || savedLead.nombre || "",
      correo: session?.user?.email || metadata.email || savedLead.correo || "",
      telefono: String(metadata.telefono || savedLead.telefono || "").replace(/^\+52\s*/, ""),
    };
  }, [showLeadForm]);

  const [lead, setLead] = useState(initialLead);

  const isFilteredLeadPlan = FILTERED_LEAD_PLANS.includes(
    String(property?.plan || "").toLowerCase()
  );
  const ownerNumber = cleanPhone(property?.contacto?.telefono);
  const destinationNumber =
    property && !isFilteredLeadPlan && ownerNumber ? ownerNumber : WHATSAPP_NUMBER;

  const getWhatsAppLink = () => {
    const rentTaxLabel = property ? getRentTaxLabel(property) : "";
    const likedSummary = getLikedPropertiesSummary();
    const favoritesText = likedSummary
      ? `\n\nPropiedades que marqué con me gusta:\n${likedSummary}`
      : "";

    const message = property
      ? `Hola, me interesa esta propiedad:
${property.title}
${property.location}
${formatPrice(property)}${rentTaxLabel ? ` ${rentTaxLabel}` : ""}
${window.location.href}${favoritesText}`
      : `${DEFAULT_MESSAGE}${favoritesText}`;

    return `https://wa.me/${destinationNumber}?text=${encodeURIComponent(message)}`;
  };

  const openWhatsApp = () => {
    window.open(getWhatsAppLink(), "_blank", "noopener,noreferrer");
  };

  const updateLead = (field, value) => {
    setLead((prev) => ({ ...prev, [field]: value }));
  };

  const sendLeadNotice = async (leadData) => {
    if (!property) return;

    await emailjs.send(
      "service_h82m5ln",
      "template_jpxs5ip",
      {
        nombre: leadData.nombre,
        telefono: `+52 ${leadData.telefono}`.trim(),
        correo: leadData.correo,
        tipo: property.type || property.ribbon || "Propiedad",
        precio: property.price || "",
        ubicacion: property.location || "",
        descripcion: `Buscador interesado: ${property.title || "Propiedad"}`,
        servicios: property.servicios || "",
        disponibilidad: property.disponibilidad || "",
        restricciones: property.restricciones || "",
        negocios_misma_plaza: "",
        medidas: property.medidas || "",
        estado_revision: isFilteredLeadPlan
          ? "NUEVO LEAD FILTRADO PARA TULOCALMX"
          : "NUEVO CONTACTO DIRECTO DE BUSCADOR",
        revision_alertas: isFilteredLeadPlan
          ? `Lead para filtrar antes de pasarlo al anunciante. Propiedad: ${property.title || ""}`
          : `El buscador quiere contactar al anunciante. Propiedad: ${property.title || ""}`,
        revision_score: "",
        imagenes: "",
        videos: "",
        planos: "",
        url_publicacion: window.location.href,
      },
      "nDzg6xYGN7OO8id3F"
    );
  };

  const handleContactClick = async (event) => {
    event.preventDefault();

    if (!property) {
      openWhatsApp();
      return;
    }

    const session = getAuthSession();
    const metadata = session?.user?.user_metadata || {};
    const savedLead = getSavedLead();
    const leadData = {
      nombre: metadata.nombre || metadata.full_name || metadata.name || savedLead.nombre || "",
      correo: session?.user?.email || metadata.email || savedLead.correo || "",
      telefono: String(metadata.telefono || savedLead.telefono || "").replace(/^\+52\s*/, ""),
    };

    if (!leadData.correo || !leadData.telefono) {
      setLead(leadData);
      setShowLeadForm(true);
      return;
    }

    try {
      await sendLeadNotice(leadData);
    } catch (error) {
      console.log("No se pudo enviar el aviso de contacto:", error);
    }
    openWhatsApp();
  };

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    setIsSendingLead(true);
    setLeadError("");

    const cleanLead = {
      nombre: lead.nombre.trim(),
      correo: lead.correo.trim(),
      telefono: cleanPhone(lead.telefono),
    };

    if (cleanLead.telefono.length < 10) {
      setLeadError("Escribe un WhatsApp válido de 10 dígitos.");
      setIsSendingLead(false);
      return;
    }

    try {
      localStorage.setItem(CONTACT_LEAD_KEY, JSON.stringify(cleanLead));

      const session = getAuthSession();
      if (session?.access_token) {
        await updateUserMetadata({
          accessToken: session.access_token,
          metadata: {
            ...(session.user?.user_metadata || {}),
            nombre: cleanLead.nombre,
            telefono: `+52 ${cleanLead.telefono}`,
            uso_cuenta: "buscar",
            interes_buscar: true,
          },
        });
      }

      await sendLeadNotice(cleanLead);
      setShowLeadForm(false);
      openWhatsApp();
    } catch (error) {
      setLeadError("No se pudieron guardar tus datos. Intenta de nuevo.");
    } finally {
      setIsSendingLead(false);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-3"
        data-testid="floating-whatsapp-container"
      >
        {showTooltip && !isDismissed && (
          <div className="relative max-w-[280px] animate-scale-in rounded-2xl bg-white p-4 shadow-2xl">
            <button
              onClick={() => setIsDismissed(true)}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 transition-colors hover:bg-gray-200"
            >
              <X className="h-3 w-3 text-gray-500" />
            </button>

            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#25D366]">
                <FaWhatsapp className="h-5 w-5 text-white" />
              </div>

              <div>
                <p className="text-sm font-semibold text-[#0F172A]">
                  ¿Necesitas ayuda?
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  Escríbenos por WhatsApp y te respondemos en minutos.
                </p>
              </div>
            </div>
          </div>
        )}

        <a
          href={getWhatsAppLink()}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[10px] font-black text-slate-800 shadow-md transition hover:bg-slate-50"
          onClick={handleContactClick}
          onMouseEnter={() => {}}
          onMouseLeave={() => {}}
        >
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#25D366]">
            <FaWhatsapp className="h-3 w-3 text-white" />
          </span>
          <span>¿Te ayudamos?</span>
        </a>
      </div>

      {showLeadForm && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 px-4 py-6"
          onClick={() => setShowLeadForm(false)}
        >
          <form
            onSubmit={handleLeadSubmit}
            onClick={(event) => event.stopPropagation()}
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-black text-slate-950">
                  Contactar anunciante
                </h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  {isFilteredLeadPlan
                    ? "Déjanos tus datos y Tulocalmx revisará tu solicitud para darte seguimiento."
                    : "Déjanos tus datos y te abrimos WhatsApp directo con el anunciante."}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowLeadForm(false)}
                className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <input
                required
                type="text"
                placeholder="Nombre completo"
                value={lead.nombre}
                onChange={(event) => updateLead("nombre", event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
              />
              <input
                required
                type="email"
                placeholder="Correo electrónico"
                value={lead.correo}
                onChange={(event) => updateLead("correo", event.target.value)}
                className="w-full rounded-lg border border-slate-200 p-3 outline-none focus:border-[#0056D2]"
              />
              <div className="flex overflow-hidden rounded-lg border border-slate-200 bg-white focus-within:border-[#0056D2]">
                <span className="flex items-center border-r border-slate-200 bg-slate-50 px-3 text-sm font-black text-slate-700">
                  +52
                </span>
                <input
                  required
                  type="tel"
                  placeholder="WhatsApp"
                  value={lead.telefono}
                  onChange={(event) => updateLead("telefono", event.target.value)}
                  className="w-full p-3 outline-none"
                />
              </div>

              {leadError && (
                <p className="rounded-lg bg-red-50 p-3 text-sm font-semibold text-red-700">
                  {leadError}
                </p>
              )}

              <button
                type="submit"
                disabled={isSendingLead}
                className="w-full rounded-lg bg-[#25D366] p-3 font-black text-white disabled:bg-emerald-300"
              >
                {isSendingLead ? "Abriendo..." : "Continuar a WhatsApp"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default FloatingWhatsApp;
